import { Component } from '@theme/component';

/**
 * A horizontally scrolling row with previous/next buttons that move it by one view.
 * Sets `data-overflowing` when there is more to scroll, and disables each button at its end.
 *
 * @typedef {object} ScrollRowRefs
 * @property {HTMLElement} track - The scrolling list.
 * @property {HTMLButtonElement} [previous] - Moves back one view.
 * @property {HTMLButtonElement} [next] - Moves forward one view.
 *
 * @extends {Component<ScrollRowRefs>}
 */
class ScrollRow extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  /** Moves the row forward by one view. */
  next() {
    this.#page(1);
  }

  /** Moves the row back by one view. */
  previous() {
    this.#page(-1);
  }

  #bind() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.refs.track.addEventListener('scroll', this.#update, { passive: true, signal });
    window.addEventListener('resize', this.#update, { signal });
    this.#update();
  }

  /** @param {number} direction - 1 for forward, -1 for back. */
  #page(direction) {
    const { track } = this.refs;
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollBy({
      left: track.clientWidth * direction * (isRtl ? -1 : 1),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  #update = () => {
    const { track, previous, next } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    // RTL rows report negative scroll positions.
    const position = Math.abs(track.scrollLeft);

    this.toggleAttribute('data-overflowing', max > 2);
    if (previous) previous.disabled = position <= 2;
    if (next) next.disabled = position >= max - 2;
  };
}

if (!customElements.get('scroll-row')) {
  customElements.define('scroll-row', ScrollRow);
}
